import React from "react";
import { Trash2, Package } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import ProductImageUpload from "./ProductImageUpload";

const MAX_DESC = 1000;

const ProductLineCard = ({
  index,
  value,
  onChange,
  onRemove,
  canRemove,
  categories,
}) => {
  const patch = (field, val) => onChange({ ...value, [field]: val });

  const descLen = (value.description || "").length;
  const descOver = descLen > MAX_DESC;

  return (
    <div
      data-testid={`product-line-card-${index}`}
      className="rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-base font-bold text-[#1E3A5F]">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-cyan-600 text-white text-sm">
            {index + 1}
          </span>
          <Package className="w-4 h-4 text-slate-500" aria-hidden="true" />
          Product #{index + 1}
        </h4>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            data-testid={`product-line-remove-${index}`}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Remove product
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label
            htmlFor={`pr-desc-${index}`}
            className="text-sm font-semibold text-slate-700"
          >
            Description <span className="text-red-600">*</span>
          </Label>
          <Textarea
            id={`pr-desc-${index}`}
            data-testid={`pr-desc-${index}`}
            required
            rows={3}
            maxLength={MAX_DESC + 50}
            placeholder="Describe the product you need quoted (features, purpose, technical details, etc.)"
            value={value.description || ""}
            onChange={(e) => patch("description", e.target.value)}
          />
          <div className={`mt-1 text-xs ${descOver ? "text-red-700" : "text-slate-500"}`}>
            {descLen}/{MAX_DESC} characters
          </div>
        </div>
        <div>
          <Label
            htmlFor={`pr-brand-${index}`}
            className="text-sm font-semibold text-slate-700"
          >
            Brand
          </Label>
          <Input
            id={`pr-brand-${index}`}
            data-testid={`pr-brand-${index}`}
            value={value.brand || ""}
            onChange={(e) => patch("brand", e.target.value)}
            placeholder="e.g. Bosch"
          />
        </div>
        <div>
          <Label
            htmlFor={`pr-model-${index}`}
            className="text-sm font-semibold text-slate-700"
          >
            Model
          </Label>
          <Input
            id={`pr-model-${index}`}
            data-testid={`pr-model-${index}`}
            value={value.model || ""}
            onChange={(e) => patch("model", e.target.value)}
            placeholder="e.g. GBH 2-26"
          />
        </div>
        <div>
          <Label
            htmlFor={`pr-manufacturer-${index}`}
            className="text-sm font-semibold text-slate-700"
          >
            Manufacturer
          </Label>
          <Input
            id={`pr-manufacturer-${index}`}
            data-testid={`pr-manufacturer-${index}`}
            value={value.manufacturer || ""}
            onChange={(e) => patch("manufacturer", e.target.value)}
            placeholder="Manufacturer / reference"
          />
        </div>
        <div>
          <Label
            htmlFor={`pr-qty-${index}`}
            className="text-sm font-semibold text-slate-700"
          >
            Quantity
          </Label>
          <Input
            id={`pr-qty-${index}`}
            data-testid={`pr-qty-${index}`}
            type="number"
            min={1}
            value={value.quantity || 1}
            onChange={(e) =>
              patch("quantity", Math.max(1, parseInt(e.target.value || "1", 10)))
            }
          />
        </div>
        {categories && categories.length > 0 && (
          <div className="md:col-span-2">
            <Label
              htmlFor={`pr-category-${index}`}
              className="text-sm font-semibold text-slate-700"
            >
              Category
            </Label>
            <select
              id={`pr-category-${index}`}
              data-testid={`pr-category-${index}`}
              value={value.category_id || ""}
              onChange={(e) => patch("category_id", e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">Select a category (optional)</option>
              {categories.map((cat) => (
                <option key={cat._id || cat.id} value={cat._id || cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div>
        <Label className="text-sm font-semibold text-slate-700 block mb-1">
          Product photo
        </Label>
        <ProductImageUpload
          testId={`product-image-upload-${index}`}
          value={value.image}
          onChange={(img) => patch("image", img)}
        />
      </div>
    </div>
  );
};

export default ProductLineCard;
